import {FC, useState} from 'react'
import {BaseModal} from '../../BaseModal/BaseModal'
import {Input} from '../../../Input/Input'
import {Button} from '../../../Button/Button'
import styles from './editVariacao.module.css'
import {POST} from '../../../../services/api'
import uuid from 'react-uuid'
import {Loading} from '../../../../components/Loading/Loading'
import {Notification} from '../../../../components/Notification/Notification'
import {i18n} from '../../../../translate/i18n'

type Props = {
  onCloseAction: () => void
  title: string
  item: {
    id: string
    name: string
    inventory: string
    price: string
    costPrice: string
    promotionalPrice: string
    status: boolean
    img: string | File
    height: number
    width: number
    depth: number
    weight: number
    sku: string
    referenceCode: string
    gtin: string
    mpn: string
    ncm: string
    minimumStock: string
    productAvailability: string
    productOutOfStock: string
    locationId: string
  }
  formatSize: string
  onSave?: (item: any, propagateImage?: boolean) => void
  locations: any
  setVariacoesComErro: (variacoesComErro: string[] | ((prev: string[]) => string[])) => void
  skuEdit?: boolean
  hasERP?: boolean
}

const EditVariacao: FC<Props> = ({
  onCloseAction,
  title,
  item,
  onSave,
  formatSize,
  locations,
  setVariacoesComErro,
  skuEdit = true,
  hasERP = false,
}) => {
  const [loading, setLoading] = useState(false)
  const [editedItem, setEditedItem] = useState(item)
  const [propagateImage, setPropagateImage] = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [notification, setNotification] = useState<{
    message: string
    type: 'success' | 'error'
    show: boolean
  }>({
    message: '',
    type: 'success',
    show: false,
  })
  const [imgPreview, setImgPreview] = useState<string | null>(
    typeof item.img === 'string'
      ? item.img
      : item.img instanceof File
      ? URL.createObjectURL(item.img)
      : null
  )

  const handleChange = (field: string, value: any) => {
    setEditedItem((prev) => ({...prev, [field]: value}))
    if (field === 'img') {
      if (value && value instanceof File) {
        setImgPreview(URL.createObjectURL(value))
      } else if (typeof value === 'string') {
        setImgPreview(value)
      }
    }
  }

  const processImageWithAPI = async (file: File, width: number, height: number) => {
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('width', width.toString())
      formData.append('height', height.toString())

      const response = await POST('product/process-image_v2', formData)

      if (response.success) {
        const base64String = response.data.file.image
        const base64Data = base64String.replace(/^data:image\/\w+;base64,/, '')
        const byteCharacters = atob(base64Data)
        const byteNumbers = new Array(byteCharacters.length)
          .fill(0)
          .map((_, i) => byteCharacters.charCodeAt(i))
        const byteArray = new Uint8Array(byteNumbers)
        const blob = new Blob([byteArray], {type: response.data.file.type})

        return new File([blob], response.data.file.name, {type: response.data.file.type})
      } else {
        throw new Error(response.error || 'Erro ao processar imagem')
      }
    } catch (error) {
      console.error('Erro ao processar imagem:', error)
      throw error
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0]
    if (file) {
      try {
        setUploadingImage(true)
        const requiredWidth = formatSize === 'square' ? 640 : 600
        const requiredHeight = formatSize === 'square' ? 640 : 900
        const processedFile = await processImageWithAPI(file, requiredWidth, requiredHeight)
        handleChange('img', processedFile)
      } catch (error) {
        console.error('Erro ao processar imagem:', error)
        e.target.value = ''
        setNotification({
          message: 'Erro ao processar imagem. Tente novamente.',
          type: 'error',
          show: true,
        })
      } finally {
        setUploadingImage(false)
        e.target.value = ''
      }
    }
  }

  const handleRemoveImage = () => {
    handleChange('img', '')
    setImgPreview(null)
  }

  const handleSave = async () => {
    setLoading(true)
    try {
      let finalImg = editedItem.img

      if (editedItem.img instanceof File) {
        setUploadingImage(true)
        const formData = new FormData()
        formData.append('file', editedItem.img)
        formData.append('nameImage', `variation-${uuid()}.jpg`)
        const imageRes = await POST('/upload-images/upload-one', formData)
        setUploadingImage(false)

        if (imageRes.success) {
          finalImg = imageRes.image
        } else {
          throw new Error(imageRes.error || 'Erro ao fazer upload da imagem')
        }
      }

      const itemToSave = {
        ...editedItem,
        img: finalImg,
      }

      if (onSave) {
        onSave(itemToSave, propagateImage)
      }
      onCloseAction()
    } catch (error) {
      console.error('Erro ao salvar variação:', error)
      setNotification({
        message: 'Erro ao salvar variação. Tente novamente.',
        type: 'error',
        show: true,
      })
    } finally {
      setLoading(false)
      setPropagateImage(false)
    }
  }

  return (
    <BaseModal onCloseAction={onCloseAction} title={title}>
      {loading ? (
        <Loading />
      ) : (
        <div className={styles.modalContent}>
          <label className='form-label fw-normal'>Imagem da Variação</label>
          {imgPreview && !uploadingImage && (
            <button
              type='button'
              className={styles.removeImageX}
              onClick={handleRemoveImage}
              aria-label='Remover imagem'
            >
              ×
            </button>
          )}
          <div className={styles.imgPreviewContainer}>
            <input
              type='file'
              accept='image/*'
              onChange={handleFileChange}
              className={styles.inputEdit}
              disabled={uploadingImage}
            />
            {uploadingImage ? (
              <div className={styles.loadingContainer}>
                <div className={styles.loadingContent}>
                  <Loading />
                  <span>Imagem sendo processada</span>
                </div>
              </div>
            ) : imgPreview ? (
              <img src={imgPreview} alt='Preview' className={styles.imgPreview} />
            ) : (
              <div className={styles.inputPlaceholder}>
                <i className='bi bi-image fs-1 text-gray-400'></i>
                <span>Clique para adicionar uma imagem</span>
              </div>
            )}
          </div>

          {editedItem.img && (
            <div className={styles.propagateImageContainer}>
              <label className={styles.propagateImageLabel}>
                <input
                  type='checkbox'
                  checked={propagateImage}
                  onChange={(e) => setPropagateImage(e.target.checked)}
                  className={styles.propagateImageCheckbox}
                />
                <span>Usar mesma foto para todos os tamanhos</span>
              </label>
            </div>
          )}

          <div className={`pb-9 ${styles.infoContainer}`}>
            <Input
              label='Estoque'
              value={editedItem.inventory}
              inputAttr={{type: 'default'}}
              placeholder='Digite o estoque'
              change={(v: any) => handleChange('inventory', v)}
            />
            <Input
              inputAttr={{
                type: 'money',
              }}
              currency='BRL'
              locale='pt-BR'
              label='Preço de Venda'
              value={editedItem.price}
              placeholder='Digite o preço de venda'
              change={(v: number) => {
                handleChange('price', v)
                if (v) {
                  setVariacoesComErro((prev: string[]) =>
                    prev.filter((id: string) => id !== item.id)
                  )
                }
              }}
              disabled={hasERP}
            />
            <Input
              inputAttr={{
                type: 'money',
              }}
              currency='BRL'
              locale='pt-BR'
              label='Preço de Custo'
              value={editedItem.costPrice}
              placeholder='Digite o preço de custo'
              change={(v: any) => handleChange('costPrice', v)}
              disabled={hasERP}
            />
            <Input
              inputAttr={{
                type: 'money',
              }}
              currency='BRL'
              locale='pt-BR'
              label='Preço Promocional'
              value={editedItem.promotionalPrice}
              placeholder='Digite o preço promocional'
              change={(v: any) => handleChange('promotionalPrice', v)}
              disabled={hasERP}
            />
            <Input
              label='Status'
              value={editedItem.status}
              checked={editedItem.status}
              inputAttr={{type: 'checkbox'}}
              checkSwift={true}
              change={(v: boolean) => handleChange('status', v)}
            />
            <Input
              label='Altura (cm)'
              value={editedItem.height}
              inputAttr={{type: 'number'}}
              placeholder='Digite a altura'
              change={(v: any) => handleChange('height', v)}
              disabled={hasERP}
            />
            <Input
              label='Largura (cm)'
              value={editedItem.width}
              inputAttr={{type: 'number'}}
              placeholder='Digite a largura'
              change={(v: any) => handleChange('width', v)}
              disabled={hasERP}
            />
            <Input
              label='Profundidade (cm)'
              value={editedItem.depth}
              inputAttr={{type: 'number'}}
              placeholder='Digite a profundidade'
              change={(v: any) => handleChange('depth', v)}
              disabled={hasERP}
            />
            <Input
              label='Peso (Kg)'
              value={editedItem.weight}
              inputAttr={{type: 'number'}}
              placeholder='Digite o peso'
              change={(v: any) => handleChange('weight', v)}
              disabled={hasERP}
            />
            {skuEdit && (
              <Input
                label='SKU'
                value={editedItem.sku}
                inputAttr={{type: 'default'}}
                placeholder='Digite o SKU'
                change={(v: any) => handleChange('sku', v)}
              />
            )}
            <Input
              label='Código de Referência'
              value={editedItem.referenceCode}
              inputAttr={{type: 'default'}}
              placeholder='Digite o código de referência'
              change={(v: any) => handleChange('referenceCode', v)}
              disabled={hasERP}
            />
            <Input
              label='GTIN'
              value={editedItem.gtin}
              inputAttr={{type: 'default'}}
              placeholder='Digite o GTIN'
              change={(v: any) => handleChange('gtin', v)}
              disabled={hasERP}
            />
            <Input
              label='MPN'
              value={editedItem.mpn}
              inputAttr={{type: 'default'}}
              placeholder='Digite o MPN'
              change={(v: any) => handleChange('mpn', v)}
              disabled={hasERP}
            />
            <Input
              label='NCM'
              value={editedItem.ncm}
              inputAttr={{type: 'default'}}
              placeholder='Digite o NCM'
              change={(v: any) => handleChange('ncm', v)}
              disabled={hasERP}
            />
            <Input
              label='Estoque Mínimo'
              value={editedItem.minimumStock}
              inputAttr={{type: 'number'}}
              placeholder='Digite o estoque mínimo'
              change={(v: any) => handleChange('minimumStock', v)}
            />
            <Input
              inputAttr={{
                type: 'select',
              }}
              value={editedItem.productAvailability}
              change={(v: any) => handleChange('productAvailability', v)}
              label='Disponibilidade do Produto'
              options={[
                {select: 'Disponibilidade imediata', value: 0},
                {select: 'Disponibilidade para 1 dia útil', value: 1},
                {select: 'Disponibilidade para 2 dias úteis', value: 2},
                {select: 'Disponibilidade para 3 dias úteis', value: 3},
                {select: 'Disponibilidade para 4 dias úteis', value: 4},
                {select: 'Disponibilidade para 5 dias úteis', value: 5},
                {select: 'Disponibilidade para 6 dias úteis', value: 6},
                {select: 'Disponibilidade para 7 dias úteis', value: 7},
                {select: 'Disponibilidade para 8 dias úteis', value: 8},
                {select: 'Disponibilidade para 9 dias úteis', value: 9},
                {select: 'Disponibilidade para 10 dias úteis', value: 10},
                {select: 'Disponibilidade para 15 dias úteis', value: 15},
                {select: 'Disponibilidade para 20 dias úteis', value: 20},
                {select: 'Disponibilidade para 25 dias úteis', value: 25},
                {select: 'Disponibilidade para 30 dias úteis', value: 30},
                {select: 'Disponibilidade para 45 dias úteis', value: 45},
                {select: 'Disponibilidade para 60 dias úteis', value: 60},
                {select: 'Disponibilidade para 90 dias úteis', value: 90},
              ]}
              disabled={hasERP}
            />
            <Input
              inputAttr={{
                type: 'select',
              }}
              value={editedItem.productOutOfStock}
              change={(v: any) => handleChange('productOutOfStock', v)}
              label='Produto Fora de Estoque'
              options={[
                {select: 'Continuar vendendo normalmente', value: 0},
                {select: 'Tornar o produto indisponível', value: -1},
                {select: 'Mudar a disponibilidade para 1 dia útil', value: 1},
                {select: 'Mudar a disponibilidade para 2 dias úteis', value: 2},
                {select: 'Mudar a disponibilidade para 3 dias úteis', value: 3},
                {select: 'Mudar a disponibilidade para 4 dias úteis', value: 4},
                {select: 'Mudar a disponibilidade para 5 dias úteis', value: 5},
                {select: 'Mudar a disponibilidade para 6 dias úteis', value: 6},
                {select: 'Mudar a disponibilidade para 7 dias úteis', value: 7},
                {select: 'Mudar a disponibilidade para 8 dias úteis', value: 8},
                {select: 'Mudar a disponibilidade para 9 dias úteis', value: 9},
                {select: 'Mudar a disponibilidade para 10 dias úteis', value: 10},
                {select: 'Mudar a disponibilidade para 15 dias úteis', value: 15},
                {select: 'Mudar a disponibilidade para 20 dias úteis', value: 20},
                {select: 'Mudar a disponibilidade para 25 dias úteis', value: 25},
                {select: 'Mudar a disponibilidade para 30 dias úteis', value: 30},
                {select: 'Mudar a disponibilidade para 45 dias úteis', value: 45},
                {select: 'Mudar a disponibilidade para 60 dias úteis', value: 60},
                {select: 'Mudar a disponibilidade para 90 dias úteis', value: 90},
              ]}
              disabled={hasERP}
            />
            {/* <Input
              inputAttr={{
                type: 'select',
              }}
              label='Modalidade'
              value={editedItem.locationId}
              options={locations}
              placeholder='Digite a modalidade'
              change={(v: any) => handleChange('locationId', v)}
            /> */}
          </div>
          <div className={styles.footer}>
            <Button
              text={loading || uploadingImage ? 'Aguarde...' : i18n.t(`buttons.save`)}
              color='primary'
              size='small'
              horizontalPadding={5}
              btnAttr={{
                type: 'button',
              }}
              click={handleSave}
              btnClass='fw-bolder'
              disabled={loading || uploadingImage}
            />
          </div>
        </div>
      )}
      <Notification
        message={notification.message}
        type={notification.type}
        show={notification.show}
        onClose={() => setNotification((prev) => ({...prev, show: false}))}
      />
    </BaseModal>
  )
}

export {EditVariacao}
